import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, MessageSquare } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { toast } from 'react-toastify';
import { emailService } from '../../../services/emailService';

interface ContactForm {
  name: string;
  email: string;
  restaurant: string;
  message: string;
}

const Contact: React.FC = () => {
  const [form, setForm] = useState<ContactForm>({
    name: '',
    email: '',
    restaurant: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!form.name || !form.email || !form.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Préparer le contenu de l'email
      const subject = `Contact EspaceResto - ${form.restaurant || 'Nouveau prospect'}`;
      const content = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f9f9f9;">
          <div style="background-color: white; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
            <h1 style="color: #2563eb; text-align: center; margin-bottom: 30px; border-bottom: 3px solid #2563eb; padding-bottom: 15px;">
              📧 NOUVEAU MESSAGE DE CONTACT - EspaceResto
            </h1>
            
            <div style="margin-bottom: 25px;">
              <h2 style="color: #1f2937; background-color: #f3f4f6; padding: 10px; border-left: 4px solid #2563eb; margin: 0 0 15px 0;">
                👤 INFORMATIONS DU CONTACT
              </h2>
              <ul style="list-style: none; padding: 0; margin: 0;">
                <li style="padding: 8px 0; border-bottom: 1px solid #e5e7eb;"><strong>Nom:</strong> ${form.name}</li>
                <li style="padding: 8px 0; border-bottom: 1px solid #e5e7eb;"><strong>Email:</strong> <a href="mailto:${form.email}" style="color: #2563eb;">${form.email}</a></li>
                <li style="padding: 8px 0; border-bottom: 1px solid #e5e7eb;"><strong>Restaurant:</strong> ${form.restaurant || 'Non spécifié'}</li>
                <li style="padding: 8px 0;"><strong>Date:</strong> ${new Date().toLocaleString('fr-FR')}</li>
              </ul>
            </div>
            
            <div style="margin-bottom: 25px;">
              <h2 style="color: #1f2937; background-color: #f3f4f6; padding: 10px; border-left: 4px solid #2563eb; margin: 0 0 15px 0;">
                💬 MESSAGE
              </h2>
              <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; border-left: 4px solid #10b981; line-height: 1.6;">
                ${form.message.replace(/\n/g, '<br>')}
              </div>
            </div>
            

          </div>
        </div>
      `;
      
      // Envoyer l'email via SendGrid
      const result = await emailService.sendEmail({
        to: 'contact@espaceresto.fr',
        subject,
        content,
        from: 'contact@espaceresto.fr'
      });
      
      if (result.success) {
        toast.success('Votre message a été envoyé avec succès. Nous vous répondrons rapidement');
        // Reset form
        setForm({ name: '', email: '', restaurant: '', message: '' });
      } else {
        throw new Error(result.error || 'Erreur lors de l\'envoi');
      }
    } catch (error: any) {
      console.error('Erreur envoi email:', error);
      toast.error('Erreur lors de l\'envoi du message. Veuillez réessayer');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 bg-white min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-light text-gray-900 mb-4 tracking-tight">
            Contactez-nous
          </h2>
          
          <div className="w-16 h-1 bg-gradient-to-r from-orange-400 to-orange-500 mx-auto mb-6"></div>
          
          <p className="text-lg text-gray-600 max-w-2xl mx-auto font-light leading-relaxed">
            Une question ? Un projet ? Notre équipe est là pour vous accompagner.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-light text-gray-900 mb-12 tracking-tight">
              Parlons de votre projet
            </h3>
            
            <div className="space-y-8">
              <div className="flex items-start space-x-5">
                <div className="bg-gray-50 p-4 rounded-full">
                  <Mail className="h-5 w-5 text-gray-700" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Email</h4>
                  <p className="text-gray-700 font-light">contact@espaceresto.fr</p>
                  <p className="text-sm text-gray-500 font-light">Réponse sous 24h</p>
                </div>
              </div>

              <div className="flex items-start space-x-5">
                <div className="bg-gray-50 p-4 rounded-full">
                  <Phone className="h-5 w-5 text-gray-700" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Téléphone</h4>
                  <p className="text-gray-700 font-light">06 10 48 95 43</p>
                  <p className="text-sm text-gray-500 font-light">Lun-Ven 9h-18h</p>
                </div>
              </div>

              <div className="flex items-start space-x-5">
                <div className="bg-gray-50 p-4 rounded-full">
                  <MessageSquare className="h-5 w-5 text-gray-700" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Chat en direct</h4>
                  <p className="text-gray-700 font-light">Support instantané</p>
                  <p className="text-sm text-gray-500 font-light">Disponible dans votre dashboard</p>
                </div>
              </div>

              <div className="flex items-start space-x-5">
                <div className="bg-gray-50 p-4 rounded-full">
                  <MapPin className="h-5 w-5 text-gray-700" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Adresse</h4>
                  <p className="text-gray-700 font-light">Rue Jean Pierre Jean</p>
                  <p className="text-gray-700 font-light">57070 Metz, France</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 p-8 bg-gray-50 rounded-2xl border border-gray-100">
              <h4 className="font-medium text-gray-900 mb-3">Besoin d'une démo ?</h4>
              <p className="text-gray-600 mb-6 font-light leading-relaxed">Découvrez EspaceResto en action avec une démonstration personnalisée.</p>
              <Button 
                onClick={() => window.open('https://cal.com/espaceresto.fr/demo', '_blank')}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-xl font-medium transition-all duration-200 hover:shadow-lg"
              >
                Réserver une démo
              </Button>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="bg-gray-50 rounded-3xl p-10 border border-gray-100"
          >
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-3">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all duration-200 font-light"
                    placeholder="Votre nom"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-3">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={form.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all duration-200 font-light"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="restaurant" className="block text-sm font-medium text-gray-700 mb-3">
                  Nom du restaurant
                </label>
                <input
                  type="text"
                  id="restaurant"
                  name="restaurant"
                  value={form.restaurant}
                  onChange={handleInputChange}
                  className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all duration-200 font-light"
                  placeholder="Le nom de votre établissement"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-3">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all duration-200 resize-none font-light"
                  placeholder="Décrivez votre projet ou posez votre question..."
                />
              </div>

              <Button
                type="submit"
                loading={isSubmitting}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-xl font-medium transition-all duration-200 w-full hover:shadow-lg"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;